// SPDX-License-Identifier: LGPL-2.1-or-later
// Plain-data vectors. Everything in Formverk's data model is structured-cloneable (no classes), so it
// can travel between the UI thread and kernel workers with postMessage unchanged.

/** 2D point/vector in sketch-plane millimetres. */
export interface Vec2 {
  readonly x: number;
  readonly y: number;
}

/** 3D point/vector in millimetres. */
export interface Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export const vec2 = (x: number, y: number): Vec2 => ({ x, y });
export const vec3 = (x: number, y: number, z: number): Vec3 => ({ x, y, z });

export const V2 = {
  zero: { x: 0, y: 0 } as Vec2,
  add: (a: Vec2, b: Vec2): Vec2 => ({ x: a.x + b.x, y: a.y + b.y }),
  sub: (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y }),
  scale: (a: Vec2, s: number): Vec2 => ({ x: a.x * s, y: a.y * s }),
  dot: (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y,
  /** Z component of the 3D cross product (signed parallelogram area). */
  cross: (a: Vec2, b: Vec2): number => a.x * b.y - a.y * b.x,
  length: (a: Vec2): number => Math.hypot(a.x, a.y),
  distance: (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y),
  normalize: (a: Vec2): Vec2 => {
    const l = Math.hypot(a.x, a.y);
    return l < 1e-300 ? { x: 0, y: 0 } : { x: a.x / l, y: a.y / l };
  },
  /** Counter-clockwise perpendicular. */
  perp: (a: Vec2): Vec2 => ({ x: -a.y, y: a.x }),
  /** Angle from +X in radians, (-π, π]. */
  angle: (a: Vec2): number => Math.atan2(a.y, a.x),
  fromAngle: (radians: number, length = 1): Vec2 => ({ x: Math.cos(radians) * length, y: Math.sin(radians) * length }),
  lerp: (a: Vec2, b: Vec2, t: number): Vec2 => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }),
  equals: (a: Vec2, b: Vec2, tol = 1e-9): boolean => Math.abs(a.x - b.x) <= tol && Math.abs(a.y - b.y) <= tol,
};

export const V3 = {
  zero: { x: 0, y: 0, z: 0 } as Vec3,
  unitX: { x: 1, y: 0, z: 0 } as Vec3,
  unitY: { x: 0, y: 1, z: 0 } as Vec3,
  unitZ: { x: 0, y: 0, z: 1 } as Vec3,
  add: (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z }),
  sub: (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z }),
  scale: (a: Vec3, s: number): Vec3 => ({ x: a.x * s, y: a.y * s, z: a.z * s }),
  neg: (a: Vec3): Vec3 => ({ x: -a.x, y: -a.y, z: -a.z }),
  dot: (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z,
  cross: (a: Vec3, b: Vec3): Vec3 => ({
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  }),
  length: (a: Vec3): number => Math.hypot(a.x, a.y, a.z),
  distance: (a: Vec3, b: Vec3): number => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z),
  normalize: (a: Vec3): Vec3 => {
    const l = Math.hypot(a.x, a.y, a.z);
    return l < 1e-300 ? { x: 0, y: 0, z: 0 } : { x: a.x / l, y: a.y / l, z: a.z / l };
  },
  lerp: (a: Vec3, b: Vec3, t: number): Vec3 => ({
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
  }),
  min: (a: Vec3, b: Vec3): Vec3 => ({ x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), z: Math.min(a.z, b.z) }),
  max: (a: Vec3, b: Vec3): Vec3 => ({ x: Math.max(a.x, b.x), y: Math.max(a.y, b.y), z: Math.max(a.z, b.z) }),
  /** Any unit vector perpendicular to `a`. */
  anyPerpendicular: (a: Vec3): Vec3 => {
    const n = V3.normalize(a);
    const helper = Math.abs(n.x) < 0.9 ? V3.unitX : V3.unitY;
    return V3.normalize(V3.cross(n, helper));
  },
  equals: (a: Vec3, b: Vec3, tol = 1e-9): boolean =>
    Math.abs(a.x - b.x) <= tol && Math.abs(a.y - b.y) <= tol && Math.abs(a.z - b.z) <= tol,
};
